// Smart Sash Admin Panel - Protected Route Component
// Wrapper component to protect routes requiring authentication and optional role authorization

import { getDefaultRouteForRoles } from '@/config/permissions';
import { UserRole } from '@/models';
import { useAuth } from '@/services/auth';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If provided, user must have at least one of these roles */
  allowedRoles?: UserRole[];
}

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated.
 * Redirects to default route if user lacks required roles.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user, requiresPasswordChange } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (requiresPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role authorization
  if (allowedRoles && allowedRoles.length > 0 && user) {
    const hasRequiredRole = user.roles.some((r) => allowedRoles.includes(r));
    if (!hasRequiredRole) {
      const defaultRoute = getDefaultRouteForRoles(user.roles);
      return <Navigate to={defaultRoute} replace />;
    }
  }

  // Render children if authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute;
